import { FeedbackSummary } from './supabase';
import { format } from 'date-fns';

export interface ExportData {
  summary: FeedbackSummary;
  userName: string;
  userEmail: string;
  exportDate: string;
}

export function generatePDFHTML(data: ExportData): string {
  const { summary, userName, userEmail, exportDate } = data;

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Feedback Summary - ${userName}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      line-height: 1.6;
      color: #1f2937;
      padding: 40px;
      max-width: 900px;
      margin: 0 auto;
    }

    .header {
      border-bottom: 3px solid #8CAFA8;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }

    .header-brand {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }

    .header-logo {
      width: 40px;
      height: 40px;
      background: #8CAFA8;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      color: white;
      font-size: 18px;
    }

    .header-brand-text {
      font-size: 24px;
      font-weight: 600;
      color: #8CAFA8;
      text-decoration: none;
    }

    .header h1 {
      color: #1f2937;
      font-size: 32px;
      margin-bottom: 8px;
    }

    .header .subtitle {
      color: #6b7280;
      font-size: 14px;
    }

    .header-link {
      color: #8CAFA8;
      text-decoration: none;
      font-size: 13px;
      margin-top: 4px;
      display: inline-block;
    }

    .header-link:hover {
      text-decoration: underline;
    }

    .meta-info {
      display: flex;
      justify-content: space-between;
      background: #f9fafb;
      padding: 16px;
      border-radius: 8px;
      margin-bottom: 30px;
      border-left: 4px solid #8CAFA8;
    }

    .meta-item {
      display: flex;
      flex-direction: column;
    }

    .meta-label {
      font-size: 12px;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }

    .meta-value {
      font-size: 14px;
      font-weight: 600;
      color: #1f2937;
    }

    .section {
      margin-bottom: 40px;
      page-break-inside: avoid;
    }

    .section-title {
      font-size: 24px;
      color: #1f2937;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 2px solid #e5e7eb;
    }

    .ai-summary {
      background: linear-gradient(135deg, #8CAFA8 0%, #7A9B94 100%);
      color: white;
      border-left: 4px solid #7A9B94;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 20px;
      font-size: 15px;
      line-height: 1.8;
    }

    .card {
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 16px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }

    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #1f2937;
    }

    .score-badge {
      background: #8CAFA8;
      color: white;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: 600;
    }

    .card-description {
      color: #4b5563;
      font-size: 14px;
      line-height: 1.6;
    }

    .theme-card {
      background: #fef3c7;
      border-left: 4px solid #f59e0b;
    }

    .theme-frequency {
      background: #f59e0b;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
    }

    .examples {
      margin-top: 12px;
      padding-left: 20px;
    }

    .example-item {
      color: #6b7280;
      font-size: 13px;
      margin-bottom: 6px;
      font-style: italic;
    }

    .development-card {
      background: #fef2f2;
      border-left: 4px solid #ef4444;
    }

    .suggestion {
      margin-top: 12px;
      padding: 12px;
      background: white;
      border-radius: 6px;
      font-size: 14px;
      color: #374151;
    }

    .footer {
      margin-top: 60px;
      padding-top: 20px;
      border-top: 2px solid #e5e7eb;
      text-align: center;
      color: #9ca3af;
      font-size: 12px;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      margin-bottom: 30px;
    }

    .stat-card {
      background: linear-gradient(135deg, #8CAFA8 0%, #7A9B94 100%);
      color: white;
      padding: 20px;
      border-radius: 8px;
      text-align: center;
    }

    .stat-value {
      font-size: 32px;
      font-weight: bold;
      margin-bottom: 8px;
    }

    .stat-label {
      font-size: 14px;
      opacity: 0.9;
    }

    @media print {
      body {
        padding: 20px;
      }

      .section {
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-brand">
      <div class="header-logo">M</div>
      <a href="https://getmentra.com" class="header-brand-text" target="_blank">Mentra</a>
    </div>
    <h1>Feedback Summary Report</h1>
    <p class="subtitle">AI-Powered Career Development Insights</p>
    <a href="https://getmentra.com" class="header-link" target="_blank">Visit getmentra.com</a>
  </div>

  <div class="meta-info">
    <div class="meta-item">
      <span class="meta-label">Name</span>
      <span class="meta-value">${userName}</span>
    </div>
    <div class="meta-item">
      <span class="meta-label">Email</span>
      <span class="meta-value">${userEmail}</span>
    </div>
    <div class="meta-item">
      <span class="meta-label">Export Date</span>
      <span class="meta-value">${exportDate}</span>
    </div>
    <div class="meta-item">
      <span class="meta-label">Total Responses</span>
      <span class="meta-value">${summary.total_responses}</span>
    </div>
  </div>

  ${summary.ai_summary ? `
  <div class="section">
    <h2 class="section-title">Executive Summary</h2>
    <div class="ai-summary">
      ${summary.ai_summary}
    </div>
  </div>
  ` : ''}

  <div class="section">
    <h2 class="section-title">Key Strengths</h2>
    ${summary.strengths && summary.strengths.length > 0 ?
      summary.strengths.map(strength => `
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">${strength.name}</h3>
            <span class="score-badge">${strength.score}/10</span>
          </div>
          <p class="card-description">${strength.description}</p>
        </div>
      `).join('')
      : '<p>No strengths data available</p>'
    }
  </div>

  <div class="section">
    <h2 class="section-title">Recurring Themes</h2>
    ${summary.themes && summary.themes.length > 0 ?
      summary.themes.map(theme => `
        <div class="card theme-card">
          <div class="card-header">
            <h3 class="card-title">${theme.name}</h3>
            <span class="theme-frequency">Mentioned ${theme.frequency}x</span>
          </div>
          ${theme.examples && theme.examples.length > 0 ? `
            <div class="examples">
              ${theme.examples.map(example => `
                <div class="example-item">"${example}"</div>
              `).join('')}
            </div>
          ` : ''}
        </div>
      `).join('')
      : '<p>No themes data available</p>'
    }
  </div>

  <div class="section">
    <h2 class="section-title">Development Areas</h2>
    ${summary.development_areas && summary.development_areas.length > 0 ?
      summary.development_areas.map(area => `
        <div class="card development-card">
          <h3 class="card-title">${area.area}</h3>
          <div class="suggestion">
            <strong>Suggested Action:</strong> ${area.suggestion}
          </div>
        </div>
      `).join('')
      : '<p>No development areas identified</p>'
    }
  </div>

  <div class="footer">
    <p>Generated by <a href="https://getmentra.com" style="color: #8CAFA8; text-decoration: none;">Mentra</a> - AI-Powered Career Feedback Platform</p>
    <p>This report is confidential and intended for ${userName} only.</p>
    <p style="margin-top: 8px;"><a href="https://getmentra.com" style="color: #8CAFA8; text-decoration: none;">getmentra.com</a></p>
  </div>
</body>
</html>
  `;
}

export function downloadPDF(data: ExportData): void {
  const html = generatePDFHTML(data);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const fileName = `mentra-feedback-summary-${data.userName.replace(/\s+/g, '-').toLowerCase()}-${format(new Date(), 'yyyy-MM-dd')}.html`;

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function printPDF(data: ExportData): void {
  const html = generatePDFHTML(data);
  const printWindow = window.open('', '_blank');

  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }
}
